import React, { useState, useEffect } from 'react';
import { navItems } from '../components/Sidebar';

const EC2_URL    = 'https://api.algrok.com';
const EC2_IP     = '43.204.177.231';
const LAMBDA_EC2 = 'https://f6ugtom7emqkxwt4kpjzrhgaxe0ivjvw.lambda-url.ap-south-1.on.aws/'; 

const DEFAULTS = {
  lotSizes: { 'NIFTY': 65, 'BANKNIFTY': 30, 'FINNIFTY': 40, 'SENSEX': 20 },
  marketOpen:  { h: 9,  m: 15 },
  marketClose: { h: 15, m: 29 },
  forceOpen: false,
  forceOpenFrom: '09:15',
  forceOpenTo:   '15:29',
  weekendMarketDays: [],
  forceExpiry: { NIFTY: '', BANKNIFTY: '', FINNIFTY: '', SENSEX: '' },
};

function load() {
  try {
    const saved = JSON.parse(localStorage.getItem('algrok_settings') || '{}');
    return {
      ...DEFAULTS,
      ...saved,
      lotSizes   : { ...DEFAULTS.lotSizes,    ...(saved.lotSizes    || {}) },
      forceExpiry: { ...DEFAULTS.forceExpiry, ...(saved.forceExpiry || {}) },
    };
  } catch { return DEFAULTS; }
}
function save(s) { localStorage.setItem('algrok_settings', JSON.stringify(s)); }

// Sidebar visibility. Kept in its own key because algrok_settings is read by
// other pages for market hours and lot sizes — a nav preference does not
// belong in it. Absent id = visible, so a new page added later shows up
// automatically rather than being hidden by an old saved list.
const NAV_KEY = 'algrok_nav_hidden';
// Derived from the sidebar's own nav list, so a page added there gets a
// visibility toggle here automatically — no second list to keep in step.
// Settings itself is excluded: hiding it would leave no route back.
const NAV_ITEMS = navItems
  .reduce((acc, sec) => acc.concat(sec.items || []), [])
  .filter(it => it && it.id && it.id !== 'settings')
  .map(it => [it.id, it.label]);
// Settings can never be hidden — hiding it would leave no way back.
function loadHidden() {
  try {
    const a = JSON.parse(localStorage.getItem(NAV_KEY) || '[]');
    return Array.isArray(a) ? a.filter(x => x !== 'settings') : [];
  } catch { return []; }
}

// Pull the server's list on mount so this page shows what other devices see,
// not just whatever this browser last cached.
function fetchHidden(cb) {
  fetch('https://api.algrok.com/ui-config')
    .then(r => r.json())
    .then(d => {
      const a = Array.isArray(d && d.nav_hidden) ? d.nav_hidden.filter(x => x !== 'settings') : [];
      try { localStorage.setItem(NAV_KEY, JSON.stringify(a)); } catch { /* ignore */ }
      cb(a);
    })
    .catch(() => { /* keep the cached list */ });
}
function saveHidden(a) {
  const clean = a.filter(x => x !== 'settings');
  // Cache locally first so this page reacts instantly and the list survives a
  // reload even if the POST fails, then persist to the server so every other
  // device picks it up.
  try { localStorage.setItem(NAV_KEY, JSON.stringify(clean)); } catch { /* ignore */ }
  // The event carries the new list, so the sidebars update without a second
  // round trip to the server.
  window.dispatchEvent(new CustomEvent('algrok-nav-changed', { detail: clean }));
  fetch('https://api.algrok.com/ui-config', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nav_hidden: clean }),
  }).catch(() => { /* local cache already applied; nothing to roll back */ });
}


function CalendarPicker({ onSelect, selected = [] }) {
  const today = new Date();
  const [viewDate, setViewDate] = React.useState(new Date(today.getFullYear(), today.getMonth(), 1));

  const year  = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const monthName = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][month];

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const days = [];
  for (let i = 0; i < firstDay; i++) days.push(null);
  for (let i = 1; i <= daysInMonth; i++) days.push(i);

  const fmtDate = (d) => `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;

  return (
    <div style={{ background:'#0a0f1e', border:'1px solid rgba(26,45,74,0.8)', borderRadius:8, padding:12, width:220 }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
        <button onClick={() => setViewDate(new Date(year, month-1, 1))}
          style={{ background:'none', border:'none', color:'#0ea5e9', cursor:'pointer', fontSize:16 }}>‹</button>
        <span style={{ color:'#e2e8f0', fontSize:12, fontWeight:700 }}>{monthName} {year}</span>
        <button onClick={() => setViewDate(new Date(year, month+1, 1))}
          style={{ background:'none', border:'none', color:'#0ea5e9', cursor:'pointer', fontSize:16 }}>›</button>
      </div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, textAlign:'center' }}>
        {['S','M','T','W','T','F','S'].map((d,i) => (
          <div key={i} style={{ fontSize:9, color:'#4a5568', padding:2 }}>{d}</div>
        ))}
        {days.map((d, i) => {
          if (!d) return <div key={i} />;
          const dateStr = fmtDate(d);
          const isSelected = selected.includes(dateStr);
          const isPast = new Date(year, month, d) < new Date(today.getFullYear(), today.getMonth(), today.getDate());
          return (
            <div key={i} onClick={() => !isPast && onSelect(dateStr)}
              style={{ padding:'3px 2px', borderRadius:3, fontSize:11, cursor: isPast ? 'default' : 'pointer',
                color: isPast ? '#2a3a4a' : isSelected ? '#000' : '#e2e8f0',
                background: isSelected ? '#00d4aa' : 'transparent',
                fontWeight: isSelected ? 700 : 400 }}>
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Settings() {
  const [hidden, setHidden] = useState(loadHidden);
  useEffect(() => { fetchHidden(setHidden); }, []);
  const toggleNav = (id) => {
    setHidden(prev => {
      const next = prev.indexOf(id) > -1 ? prev.filter(x => x !== id) : prev.concat([id]);
      saveHidden(next);
      return next;
    });
  };
  const [settings, setSettings] = useState(() => load());
  const [saved,    setSaved]    = useState(false);
  const [showCal,  setShowCal]  = useState(null); // 'weekend' | sym name
  const [health,   setHealth]   = useState(null);
  const [ec2,      setEc2]      = useState(null);
  const [ec2Loading, setEc2Loading] = useState(false);
  const [ec2Down,  setEc2Down]  = useState(false);
  const [lambdaState, setLambdaState] = useState(null); // {state:'running'|'stopped'|'starting'}
  const [shutdownConfirm, setShutdownConfirm] = useState(false);
  const [disk, setDisk] = useState(null);          // manual disk check result
  const [diskLoading, setDiskLoading] = useState(false);

  const checkDisk = () => {
    setDiskLoading(true);
    fetch(EC2_URL + '/ec2/disk', { signal: AbortSignal.timeout(6000) })
      .then(r => r.json())
      .then(d => { setDisk(d && d.total_mb ? d : { error: true }); setDiskLoading(false); })
      .catch(() => { setDisk({ error: true }); setDiskLoading(false); });
  };

  useEffect(() => {
    fetch(EC2_URL + '/health').then(r => r.json()).then(setHealth).catch(() => setHealth(null));
    fetch(EC2_URL + '/ec2/status', { signal: AbortSignal.timeout(5000) })
      .then(r => r.json()).then(d => { setEc2(d); setEc2Down(false); })
      .catch(() => { setEc2Down(true); fetchLambdaStatus(); });
  }, []);

  const fetchLambdaStatus = () => {
    if (!LAMBDA_EC2) return;
    fetch(LAMBDA_EC2 + '/status').then(r => r.json()).then(setLambdaState).catch(() => {});
  };

  const refreshEc2 = () => {
    fetch(EC2_URL + '/ec2/status', { signal: AbortSignal.timeout(5000) })
      .then(r => r.json()).then(d => { setEc2(d); setEc2Down(false); })
      .catch(() => { setEc2Down(true); fetchLambdaStatus(); });
  };

  const startViaLambda = () => {
    if (!LAMBDA_EC2) { alert('Lambda URL not configured. Set LAMBDA_EC2 in Settings.jsx'); return; }
    setEc2Loading(true);
    fetch(LAMBDA_EC2 + '/start', { method:'POST' })
      .then(r => r.json())
      .then(d => { setEc2Loading(false); setLambdaState({ state:'starting' }); setTimeout(refreshEc2, 30000); })
      .catch(() => setEc2Loading(false));
  };

  const doEc2 = (url, body, cb) => {
    setEc2Loading(true);
    fetch(EC2_URL + url, { method:'POST', headers:{'Content-Type':'application/json'}, body: body ? JSON.stringify(body) : undefined })
      .then(r => r.json())
      .then(d => { setEc2Loading(false); refreshEc2(); if (cb) cb(d); })
      .catch(() => setEc2Loading(false));
  };

  const update = (key, val) => setSettings(s => ({ ...s, [key]: val }));
  const updateNested = (key, sub, val) => setSettings(s => ({ ...s, [key]: { ...s[key], [sub]: val } }));

  const handleSave = () => {
    save(settings);
    // Also push the window SERVER-side. broker.py reads it from ui_config.json
    // to decide whether an order is placed normally — it used to have
    // 09:15-15:30 hardcoded, which silently turned a 15:36 exit into an AMO
    // that Kite refused. This card already owns the hours for the header
    // display, so it owns them for the order layer too rather than a second
    // card that could disagree with it.
    try {
      const two = n => String(n).padStart(2, '0');
      fetch(EC2_URL + '/ui-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          market_open:  `${two(settings.marketOpen.h)}:${two(settings.marketOpen.m)}`,
          market_close: `${two(settings.marketClose.h)}:${two(settings.marketClose.m)}`,
        }),
      }).catch(() => {});
    } catch { /* the local save already succeeded */ }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const card = { background: '#0d1526', border: '1px solid rgba(26,45,74,0.7)', borderRadius: 8, padding: 20, marginBottom: 16 };
  const label = { fontSize: 11, color: '#8899aa', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6, display: 'block' };
  const input = { background: '#080e1d', color: '#e2e8f0', border: '1px solid rgba(26,45,74,0.8)', borderRadius: 4, padding: '6px 10px', fontSize: 13, fontFamily: "'Inter', sans-serif", width: '100%' };
  const row = { display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 };

  return (
    <div style={{ padding: 24, maxWidth: 800, fontFamily: "'Inter', sans-serif", color: '#e2e8f0', overflowY: 'auto', height: '100%' }}>
      <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Settings</div>
      <div style={{ fontSize: 12, color: '#8899aa', marginBottom: 24 }}>Platform configuration · changes apply immediately on save</div>

      {/* ── System Status ─────────────────────────────── */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0ea5e9', marginBottom: 14 }}>System Status</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {[
            { label: 'EC2 Engine', value: EC2_IP, sub: health ? (health.status === 'ok' ? '🟢 Online' : '🔴 Error') : '⟳ Checking...' },
            { label: 'WebSocket', value: health?.ws_connected ? '🟢 Connected' : '🔴 Disconnected', sub: `${health?.instruments || 0} instruments` },
            { label: 'API Gateway', value: 'AWS Lambda', sub: 'ap-south-1' },
          ].map(item => (
            <div key={item.label} style={{ background: '#080e1d', borderRadius: 6, padding: 12, border: '1px solid rgba(26,45,74,0.5)' }}>
              <div style={{ fontSize: 10, color: '#8899aa', marginBottom: 4 }}>{item.label}</div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#e2e8f0' }}>{item.value}</div>
              <div style={{ fontSize: 11, color: '#00d4aa' }}>{item.sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── EC2 Control + Cost ──────────────────────── */}
      <div style={card}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0ea5e9' }}>EC2 Instance Control</div>
          <button onClick={refreshEc2} style={{ fontSize:10, color:'#556677', background:'none', border:'none', cursor:'pointer' }}>↻ Refresh</button>
        </div>

        {!ec2 && ec2Down ? (
          <div style={{ padding:16, borderRadius:6, background:'rgba(255,68,102,0.06)', border:'1px solid rgba(255,68,102,0.2)' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
              <span style={{ fontSize:20 }}>🔴</span>
              <div>
                <div style={{ fontSize:13, fontWeight:700, color:'#ff4466' }}>EC2 Instance Unreachable</div>
                <div style={{ fontSize:10, color:'#8899aa' }}>
                  {lambdaState ? `Instance state: ${lambdaState.state}` : 'Instance may be stopped or network issue'}
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <button onClick={startViaLambda} disabled={ec2Loading || (lambdaState && lambdaState.state === 'starting')}
                style={{ padding:'10px 24px', borderRadius:6, fontSize:13, fontWeight:700, cursor:'pointer',
                  background:'rgba(0,212,170,0.15)', color:'#00d4aa', border:'1px solid rgba(0,212,170,0.4)',
                  opacity: ec2Loading ? 0.5 : 1 }}>
                {ec2Loading ? '⟳ Starting...' : lambdaState?.state === 'starting' ? '⟳ Starting (~30s)...' : '▶ Start EC2 Instance'}
              </button>
              <button onClick={refreshEc2}
                style={{ padding:'10px 16px', borderRadius:6, fontSize:12, cursor:'pointer',
                  background:'rgba(14,165,233,0.1)', color:'#0ea5e9', border:'1px solid rgba(14,165,233,0.3)' }}>
                ↻ Check Again
              </button>
              {!LAMBDA_EC2 && <span style={{ fontSize:9, color:'#fbbf24' }}>⚠ Lambda URL not set — deploy lambda_ec2_control.py first</span>}
            </div>
          </div>
        ) : !ec2 ? (
          <div style={{ fontSize:11, color:'#556677' }}>Loading EC2 status...</div>
        ) : (
          <>
            {/* Instance info row */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr', gap:12, marginBottom:14 }}>
              <div style={{ background:'#080e1d', borderRadius:6, padding:10, border:'1px solid rgba(26,45,74,0.5)' }}>
                <div style={{ fontSize:9, color:'#8899aa' }}>Instance</div>
                <div style={{ fontSize:11, fontWeight:700, color:'#e2e8f0' }}>{ec2.instance_id || '--'}</div>
                <div style={{ fontSize:10, color:'#0ea5e9' }}>{ec2.instance_type || '--'}</div>
              </div>
              <div style={{ background:'#080e1d', borderRadius:6, padding:10, border:'1px solid rgba(26,45,74,0.5)' }}>
                <div style={{ fontSize:9, color:'#8899aa' }}>Uptime</div>
                <div style={{ fontSize:11, fontWeight:700, color:'#00d4aa' }}>{ec2.uptime || '--'}</div>
                <div style={{ fontSize:10, color:'#556677' }}>{ec2.boot_time || ''}</div>
              </div>
              <div style={{ background:'#080e1d', borderRadius:6, padding:10, border:'1px solid rgba(26,45,74,0.5)' }}>
                <div style={{ fontSize:9, color:'#8899aa' }}>This Session</div>
                <div style={{ fontSize:14, fontWeight:900, color:'#fbbf24' }}>${ec2.cost?.session_cost || '0.00'}</div>
                <div style={{ fontSize:10, color:'#556677' }}>{ec2.cost?.session_hours || 0}h × ${ec2.cost?.hourly_rate || '--'}/h</div>
              </div>
              <div style={{ background:'#080e1d', borderRadius:6, padding:10, border:'1px solid rgba(26,45,74,0.5)' }}>
                <div style={{ fontSize:9, color:'#8899aa' }}>MTD Estimated</div>
                <div style={{ fontSize:14, fontWeight:900, color:'#fbbf24' }}>${ec2.cost?.mtd_estimated || '0.00'}</div>
                <div style={{ fontSize:10, color:'#556677' }}>Billed: ${ec2.cost?.billed || '0.00'}</div>
              </div>
              <div style={{ background:'#080e1d', borderRadius:6, padding:10, border:'1px solid rgba(26,45,74,0.5)' }}>
                <div style={{ fontSize:9, color:'#8899aa' }}>Monthly Forecast</div>
                <div style={{ display:'flex', gap:6, alignItems:'baseline' }}>
                  <span style={{ fontSize:14, fontWeight:900, color: ec2.cost?.monthly_savings > 0 ? '#00d4aa' : '#fbbf24' }}>${ec2.cost?.monthly_estimated || '0.00'}</span>
                  {ec2.cost?.monthly_savings > 0 && <span style={{ fontSize:9, color:'#00d4aa' }}>(-${ec2.cost.monthly_savings})</span>}
                </div>
                <div style={{ fontSize:9, color:'#556677' }}>vs 24/7: ${ec2.cost?.monthly_24x7 || '--'}</div>
                <div style={{ fontSize:9, color:'#556677' }}>EC2: ${ec2.cost?.compute_monthly || '--'} + EBS: ${ec2.cost?.ebs_monthly || '--'}</div>
              </div>
            </div>

            {/* Disk Space — manual check (no polling) */}
            <div style={{ background:'#080e1d', borderRadius:6, padding:12, border:'1px solid rgba(26,45,74,0.5)', marginBottom:14 }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'#e2e8f0' }}>💾 Disk Space</span>
                  <button onClick={checkDisk} disabled={diskLoading}
                    style={{ padding:'6px 14px', borderRadius:6, fontSize:11, fontWeight:700, cursor:'pointer',
                      background:'rgba(14,165,233,0.12)', color:'#0ea5e9', border:'1px solid rgba(14,165,233,0.35)',
                      opacity: diskLoading ? 0.5 : 1 }}>
                    {diskLoading ? '⟳ Checking…' : 'Check Disk'}
                  </button>
                </div>
                {disk && !disk.error && (() => {
                  const pct = disk.used_pct || 0;
                  const col = pct > 85 ? '#ff4466' : pct > 70 ? '#fbbf24' : '#00d4aa';
                  return (
                    <div style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
                      <span style={{ fontSize:12, fontWeight:800, color:col }}>
                        {(disk.used_mb || 0).toLocaleString()} MB / {(disk.total_mb || 0).toLocaleString()} MB ({pct}%)
                      </span>
                      <span style={{ fontSize:10, color:'#8899aa' }}>free {(disk.free_mb || 0).toLocaleString()} MB</span>
                      <div style={{ width:140, height:8, background:'#0a1220', borderRadius:4, overflow:'hidden', border:'1px solid rgba(26,45,74,0.5)' }}>
                        <div style={{ width: Math.min(100, pct) + '%', height:'100%', background:col }} />
                      </div>
                    </div>
                  );
                })()}
                {disk && disk.error && <span style={{ fontSize:11, color:'#ff4466' }}>Check failed — is the /ec2/disk route deployed?</span>}
              </div>
            </div>

            {/* Schedule */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:14 }}>
              {/* Auto-Start */}
              <div style={{ background:'#080e1d', borderRadius:6, padding:12, border:'1px solid rgba(26,45,74,0.5)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <input type="checkbox" checked={ec2.config?.auto_start_enabled || false}
                    onChange={e => doEc2('/ec2/schedule', { auto_start_enabled: e.target.checked })}
                    style={{ width:14, height:14, cursor:'pointer' }} />
                  <span style={{ fontSize:12, fontWeight:700, color: ec2.config?.auto_start_enabled ? '#00d4aa' : '#556677' }}>
                    Auto-Start (Mon-Fri)
                  </span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:10, color:'#8899aa' }}>Start at IST:</span>
                  <input type="time" value={ec2.config?.auto_start_time || '08:45'}
                    onChange={e => doEc2('/ec2/schedule', { auto_start_time: e.target.value, auto_start_enabled: true })}
                    style={{ ...input, width:100, fontSize:11 }} />
                </div>
                <div style={{ fontSize:9, color:'#4a5568', marginTop:6 }}>EC2 starts via EventBridge rule before market open</div>
              </div>

              {/* Auto-Shutdown */}
              <div style={{ background:'#080e1d', borderRadius:6, padding:12, border:'1px solid rgba(26,45,74,0.5)' }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <input type="checkbox" checked={ec2.config?.auto_shutdown_enabled || false}
                    onChange={e => doEc2('/ec2/schedule', { auto_shutdown_enabled: e.target.checked })}
                    style={{ width:14, height:14, cursor:'pointer' }} />
                  <span style={{ fontSize:12, fontWeight:700, color: ec2.config?.auto_shutdown_enabled ? '#ff4466' : '#556677' }}>
                    Auto-Shutdown (Mon-Fri)
                  </span>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                  <span style={{ fontSize:10, color:'#8899aa' }}>Stop at IST:</span>
                  <input type="time" value={ec2.config?.auto_shutdown_time || '16:00'}
                    onChange={e => doEc2('/ec2/schedule', { auto_shutdown_time: e.target.value, auto_shutdown_enabled: true })}
                    style={{ ...input, width:100, fontSize:11 }} />
                </div>
                <div style={{ fontSize:9, color:'#4a5568', marginTop:6 }}>Instance stops via cron after market close</div>
              </div>
            </div>

            {/* Shutdown button */}
            <div style={{ display:'flex', gap:12, alignItems:'center' }}>
              {!shutdownConfirm ? (
                <button onClick={() => setShutdownConfirm(true)} disabled={ec2Loading}
                  style={{ padding:'8px 20px', borderRadius:6, fontSize:12, fontWeight:700, cursor:'pointer',
                    background:'rgba(255,68,102,0.12)', color:'#ff4466', border:'1px solid rgba(255,68,102,0.4)' }}>
                  ⏻ Shutdown EC2 Now
                </button>
              ) : (
                <div style={{ display:'flex', gap:8, alignItems:'center', padding:'6px 12px', borderRadius:6, background:'rgba(255,68,102,0.08)', border:'1px solid rgba(255,68,102,0.3)' }}>
                  <span style={{ fontSize:11, color:'#ff4466', fontWeight:700 }}>Are you sure? Instance will stop.</span>
                  <button onClick={() => { doEc2('/ec2/shutdown', {}, () => setShutdownConfirm(false)); }}
                    disabled={ec2Loading}
                    style={{ padding:'5px 14px', borderRadius:4, fontSize:11, fontWeight:800, cursor:'pointer',
                      background:'#ff4466', color:'#fff', border:'none' }}>
                    {ec2Loading ? 'Stopping...' : 'YES, STOP'}
                  </button>
                  <button onClick={() => setShutdownConfirm(false)}
                    style={{ padding:'5px 14px', borderRadius:4, fontSize:11, cursor:'pointer',
                      background:'none', color:'#8899aa', border:'1px solid rgba(100,100,120,0.3)' }}>
                    Cancel
                  </button>
                </div>
              )}
              <span style={{ fontSize:10, color:'#4a5568' }}>
                {ec2.region} · {ec2.current_time_ist || ''}
              </span>
            </div>
          </>
        )}
      </div>

      {/* ── Lot Sizes ─────────────────────────────────── */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0ea5e9', marginBottom: 4 }}>Lot Sizes</div>
        <div style={{ fontSize: 11, color: '#8899aa', marginBottom: 14 }}>1 order = 1 lot × lot size quantity</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          {Object.keys(settings.lotSizes).map(sym => (
            <div key={sym}>
              <label style={label}>{sym}</label>
              <input type="number" value={settings.lotSizes[sym]}
                onChange={e => updateNested('lotSizes', sym, parseInt(e.target.value) || 1)}
                style={{ ...input, width: 80 }} min={1} />
              <div style={{ fontSize: 10, color: '#4a5568', marginTop: 4 }}>qty per order</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Market Hours ──────────────────────────────── */}
      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0ea5e9', marginBottom: 4 }}>Market Hours</div>
        <div style={{ fontSize: 11, color: '#8899aa', marginBottom: 14 }}>Default: 09:15 – 15:29 IST Mon–Fri</div>

        <div style={row}>
          <div style={{ flex: 1 }}>
            <label style={label}>Market Open</label>
            <input type="time" value={`${String(settings.marketOpen.h).padStart(2,'0')}:${String(settings.marketOpen.m).padStart(2,'0')}`}
              onChange={e => { const [h,m] = e.target.value.split(':'); update('marketOpen', {h:+h,m:+m}); }}
              style={{ ...input, width: 120 }} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={label}>Market Close</label>
            <input type="time" value={`${String(settings.marketClose.h).padStart(2,'0')}:${String(settings.marketClose.m).padStart(2,'0')}`}
              onChange={e => { const [h,m] = e.target.value.split(':'); update('marketClose', {h:+h,m:+m}); }}
              style={{ ...input, width: 120 }} />
          </div>
        </div>

        {/* Force open override */}
        <div style={{ background: '#080e1d', borderRadius: 6, padding: 14, border: '1px solid rgba(26,45,74,0.5)', marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <input type="checkbox" checked={settings.forceOpen}
              onChange={e => update('forceOpen', e.target.checked)}
              style={{ width: 16, height: 16, cursor: 'pointer' }} />
            <span style={{ fontSize: 12, fontWeight: 700, color: settings.forceOpen ? '#fbbf24' : '#8899aa' }}>
              Force Market Open (Special Session Override)
            </span>
          </div>
          {settings.forceOpen && (
            <div style={row}>
              <div>
                <label style={label}>From</label>
                <input type="time" value={settings.forceOpenFrom}
                  onChange={e => update('forceOpenFrom', e.target.value)}
                  style={{ ...input, width: 120 }} />
              </div>
              <div>
                <label style={label}>To</label>
                <input type="time" value={settings.forceOpenTo}
                  onChange={e => update('forceOpenTo', e.target.value)}
                  style={{ ...input, width: 120 }} />
              </div>
              <div style={{ fontSize: 11, color: '#fbbf24', marginTop: 16 }}>
                ⚠️ Overrides default market hours check
              </div>
            </div>
          )}
        </div>

        {/* Weekend market days */}
        <div style={{ marginTop: 12 }}>
          <label style={label}>Weekend Market Days (select dates to treat as trading days)</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {settings.weekendMarketDays.map((d, i) => (
              <div key={i} style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)', borderRadius: 4, padding: '4px 10px', fontSize: 11, color: '#fbbf24', display: 'flex', alignItems: 'center', gap: 6 }}>
                {d}
                <span style={{ cursor: 'pointer', color: '#ff4466' }}
                  onClick={() => update('weekendMarketDays', settings.weekendMarketDays.filter((_,j) => j !== i))}>×</span>
              </div>
            ))}
            <button onClick={() => setShowCal(c => c === 'weekend' ? null : 'weekend')}
              style={{ padding:'4px 10px', borderRadius:4, fontSize:11, cursor:'pointer',
                background:'rgba(14,165,233,0.1)', color:'#0ea5e9', border:'1px solid rgba(14,165,233,0.3)' }}>
              + Add Date
            </button>
            {showCal === 'weekend' && (
              <div style={{ position:'absolute', zIndex:100, marginTop:4 }}>
                <CalendarPicker
                  selected={settings.weekendMarketDays}
                  onSelect={d => {
                    if (!settings.weekendMarketDays.includes(d)) {
                      update('weekendMarketDays', [...settings.weekendMarketDays, d]);
                    }
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Force Expiry ──────────────────────────────── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#0ea5e9' }}>Sidebar Menu Items</div>
          <span style={{ fontSize: 11, color: '#00d4aa' }}>{NAV_ITEMS.length - hidden.length} shown</span>
          <span style={{ fontSize: 11, color: '#556677' }}>{hidden.length} hidden</span>
          {hidden.length > 0 && (
            <button onClick={() => { setHidden([]); saveHidden([]); }}
              style={{ marginLeft: 'auto', background: 'transparent', color: '#8899aa',
                border: '1px solid rgba(26,45,74,0.8)', borderRadius: 4, padding: '4px 12px',
                fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>
              Show all
            </button>
          )}
        </div>
        <div style={{ fontSize: 11, color: '#8899aa', marginBottom: 14 }}>
          Hide pages you rarely open to shorten the menu. This only affects the sidebar —
          hidden pages still work and can be reached from the Workspace. Settings always stays visible.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(184px, 1fr))', gap: 8 }}>
          {NAV_ITEMS.map(([id, label]) => {
            const on = hidden.indexOf(id) === -1;
            return (
              <label key={id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
                background: '#080e1d', border: '1px solid rgba(26,45,74,0.8)', borderRadius: 4,
                padding: '7px 10px' }}>
                <input type="checkbox" checked={on} onChange={() => toggleNav(id)}
                  style={{ width: 14, height: 14, cursor: 'pointer' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: on ? '#e2e8f0' : '#4a5568' }}>{label}</span>
              </label>
            );
          })}
        </div>
      </div>

      <div style={card}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0ea5e9', marginBottom: 4 }}>Force Expiry Date</div>
        <div style={{ fontSize: 11, color: '#8899aa', marginBottom: 14 }}>
          Override system expiry detection. Use when exchange prepones/postpones expiry. Leave blank for auto.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          {Object.keys(settings.forceExpiry).map(sym => (
            <div key={sym} style={{ position:'relative' }}>
              <label style={label}>{sym} Expiry</label>
              <button onClick={() => setShowCal(c => c === sym ? null : sym)}
                style={{ ...input, cursor:'pointer', background:'rgba(14,165,233,0.05)',
                  color: settings.forceExpiry[sym] ? '#0ea5e9' : '#4a5568', textAlign:'left' }}>
                {settings.forceExpiry[sym] || 'Click to select date'}
              </button>
              {showCal === sym && (
                <div style={{ position:'absolute', zIndex:100, marginTop:2 }}>
                  <CalendarPicker
                    selected={settings.forceExpiry[sym] ? [settings.forceExpiry[sym]] : []}
                    onSelect={d => { updateNested('forceExpiry', sym, d); setShowCal(null); }}
                  />
                </div>
              )}
              {settings.forceExpiry[sym] && (
                <button onClick={() => updateNested('forceExpiry', sym, '')}
                  style={{ marginTop: 4, fontSize: 10, color: '#ff4466', background: 'none', border: 'none', cursor: 'pointer' }}>
                  Clear (use auto)
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── Save Button ───────────────────────────────── */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button onClick={handleSave}
          style={{ padding: '10px 32px', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: 'pointer',
            background: saved ? 'rgba(0,212,170,0.2)' : 'rgba(14,165,233,0.2)',
            color: saved ? '#00d4aa' : '#0ea5e9',
            border: `1px solid ${saved ? 'rgba(0,212,170,0.5)' : 'rgba(14,165,233,0.5)'}` }}>
          {saved ? '✅ Saved!' : 'Save Settings'}
        </button>
        <button onClick={() => { setSettings(DEFAULTS); save(DEFAULTS); setSaved(true); setTimeout(() => setSaved(false), 2000); }}
          style={{ padding: '10px 20px', borderRadius: 6, fontSize: 13, cursor: 'pointer',
            background: 'rgba(255,68,102,0.1)', color: '#ff4466', border: '1px solid rgba(255,68,102,0.3)', fontWeight: 700 }}>
          Reset Defaults
        </button>
        <span style={{ fontSize: 11, color: '#4a5568' }}>Settings saved to browser storage · applied across all pages</span>
      </div>
    </div>
  );
}
